import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Copy, CheckCircle, AlertCircle, X } from 'lucide-react';
import logoImage from './assets/logo.png';

// Custom animation keyframes
const floatKeyframes = `
  @keyframes float {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-10px);
    }
  }
`;

const pulseKeyframes = `
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.7;
    }
  }
`;

// Add keyframes to document
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = floatKeyframes + pulseKeyframes;
  document.head.appendChild(style);
}

// API configuration
const API_CONFIG = {
  API_SERVER: 'https://api.cero.gg:5010',
};

// [ISSUE 3 FIX] Define TokenForm outside of Cero
const TokenForm = ({
  ticker,
  name,
  handleTickerChange,
  handleNameChange,
  tickerError,
  nameError,
  isPosting,
  handleTokenFormSubmit,
  initialFocusRef, // For focusing the first input
}) => {
  const nameRef = useRef(null);

  useEffect(() => {
    if (initialFocusRef && initialFocusRef.current) {
      initialFocusRef.current.focus();
    }
  }, [initialFocusRef]);

  const handleTickerKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      nameRef.current?.focus();
    }
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTokenFormSubmit();
    }
  };

  return (
    <div className='space-y-6'>
      <div>
        <label className='block text-sm font-medium text-dark-text-primary mb-2'>
          Ticker Symbol
        </label>
        <div className='relative'>
          <input
            ref={initialFocusRef} // Use the passed ref for the first input
            type='text'
            value={ticker}
            onChange={handleTickerChange}
            onKeyDown={handleTickerKeyDown}
            className='w-full px-4 py-3 bg-dark-base border border-dark/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-transparent text-dark-text-primary placeholder-dark-text-secondary/50 transition-all'
            placeholder='e.g. BTC'
            maxLength={15}
          />
          {tickerError && (
            <p className='mt-2 text-sm text-red-400 flex items-center'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='h-4 w-4 mr-1'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
              {tickerError}
            </p>
          )}
        </div>
      </div>
      <div>
        <label className='block text-sm font-medium text-dark-text-primary mb-2'>
          Token Name
        </label>
        <div className='relative'>
          <input
            ref={nameRef}
            type='text'
            value={name}
            onChange={handleNameChange}
            onKeyDown={handleNameKeyDown}
            className='w-full px-4 py-3 bg-dark-base border border-dark/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-transparent text-dark-text-primary placeholder-dark-text-secondary/50 transition-all'
            placeholder='e.g. Bitcoin'
          />
          {nameError && (
            <p className='mt-2 text-sm text-red-400 flex items-center'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='h-4 w-4 mr-1'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
              {nameError}
            </p>
          )}
        </div>
      </div>
      <button
        onClick={handleTokenFormSubmit}
        disabled={isPosting}
        className={`w-full py-3 px-4 rounded-lg text-dark-base font-medium transition-all ${
          isPosting
            ? 'bg-dark-surface/80 cursor-not-allowed'
            : 'bg-gradient-to-r from-accent-primary to-accent-secondary hover:opacity-90'
        }`}
      >
        {isPosting ? (
          <div className='flex items-center justify-center'>
            <div className='animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-dark-base mr-2'></div>
            Creating Token...
          </div>
        ) : (
          'Create Token'
        )}
      </button>
    </div>
  );
};

const Cero = () => {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [isConverting, setIsConverting] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [showTokenForm, setShowTokenForm] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [xAuthUser, setXAuthUser] = useState(null);
  const [ticker, setTicker] = useState('');
  const [name, setName] = useState('');
  const [tickerError, setTickerError] = useState('');
  const [nameError, setNameError] = useState('');
  const [isPosting, setIsPosting] = useState(false);
  const [postSuccess, setPostSuccess] = useState(false);
  const [mediaContent, setMediaContent] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedPlatform, setSelectedPlatform] = useState(null);

  // [ISSUE 3 FIX] Ref for initial focus in TokenForm
  const tokenFormTickerRef = useRef(null);

  const steps = [
    { title: 'Enter URL', description: 'Paste your content URL' },
    {
      title: 'Preview & Tokenize',
      description: 'Review content & create token',
    },
    { title: 'Success', description: 'Share your token' },
  ];

  // Renamed from downloadInstagramVideo to downloadInstagramMedia
  const downloadInstagramMedia = async (postUrl) => {
    try {
      const response = await fetch(
        `${
          API_CONFIG.API_SERVER
        }/api/instagram/download?url=${encodeURIComponent(postUrl)}`
      );

      if (!response.ok) {
        throw new Error('Failed to download Instagram media');
      }

      const data = await response.json();
      if (!data.mediaUrl) {
        throw new Error('No media URL found in response');
      }
      return data.mediaUrl;
    } catch (error) {
      console.error('Error downloading Instagram media:', error);
      throw error; // Re-throw to be caught by caller
    }
  };

  const downloadTikTokVideo = async (videoUrl) => {
    try {
      const response = await fetch(
        `${API_CONFIG.API_SERVER}/api/tiktok/download?url=${encodeURIComponent(
          videoUrl
        )}`
      );

      if (!response.ok) {
        throw new Error('Failed to download video');
      }

      const data = await response.json();
      if (!data.mediaUrl) {
        throw new Error('No media URL found in response');
      }
      return data.mediaUrl;
    } catch (error) {
      console.error('Error downloading TikTok video:', error);
      throw error;
    }
  };

  // Renamed parameter from videoUrl to mediaUrl
  const prepareMediaForUpload = async (mediaUrl) => {
    try {
      // This function now expects mediaUrl to be directly fetchable (either originally or already proxied by caller)
      console.log(
        '[FRONTEND DEBUG prepareMediaForUpload] Attempting to fetch media from URL:',
        mediaUrl
      );
      const response = await fetch(mediaUrl);
      console.log(
        '[FRONTEND DEBUG prepareMediaForUpload] Fetch response status:',
        response.status,
        response.statusText
      );
      if (!response.ok) {
        let errorBody = 'Unknown error from direct media fetch';
        try {
          errorBody = await response.text();
        } catch (e) {
          /* ignore */
        }
        throw new Error(
          `Failed to fetch media from URL: ${response.status} ${response.statusText}. Body: ${errorBody}`
        );
      }
      const blob = await response.blob();
      console.log(
        '[FRONTEND DEBUG prepareMediaForUpload] Blob created. Type:',
        blob.type,
        'Size:',
        blob.size
      );

      const previewObjectUrl = URL.createObjectURL(blob);
      console.log(
        '[FRONTEND DEBUG prepareMediaForUpload] Blob URL for preview created:',
        previewObjectUrl
      );
      setMediaPreview(previewObjectUrl);

      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64data = reader.result.split(',')[1];
          console.log(
            '[FRONTEND DEBUG prepareMediaForUpload] Base64 data conversion complete. Length:',
            base64data?.length
          );
          resolve({
            mediaData: base64data,
            mediaType: blob.type,
            mediaUrl: previewObjectUrl,
          });
        };
        reader.onerror = (error) => {
          console.error(
            '[FRONTEND DEBUG prepareMediaForUpload] FileReader error:',
            error
          );
          reject(error);
        };
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error(
        '[FRONTEND DEBUG prepareMediaForUpload] Error in prepareMediaForUpload:',
        error
      );
      // Ensure mediaPreview is cleared or set to null if preparation fails critically before preview was set
      setMediaPreview(null);
      throw new Error('Failed to prepare media for upload: ' + error.message); // Re-throw with more context
    }
  };

  useEffect(() => {
    if (isConverting && url) {
      const tiktokPattern =
        /^(https?:\/\/)?(www\.)?(tiktok\.com)\/@?[a-zA-Z0-9_.]+\/video\/\d+/;
      const instagramPattern =
        /^(https?:\/\/)?(www\.)?(instagram\.com|instagr\.am)\/(p|reel|reels)\/[a-zA-Z0-9_-]+\/?/;
      const redditPattern =
        /^(https?:\/\/)?(www\.)?(reddit\.com|redd\.it)\/r\/[a-zA-Z0-9_]+\/(comments\/)?([a-zA-Z0-9_]+)(\/[\w\W]*)?$/;

      const handleConversion = async () => {
        try {
          let downloadedMediaUrl;
          let platformForMedia = selectedPlatform;
          let directMedia = false;

          if (tiktokPattern.test(url)) {
            downloadedMediaUrl = await downloadTikTokVideo(url);
            if (!platformForMedia) platformForMedia = 'tiktok';
          } else if (instagramPattern.test(url)) {
            downloadedMediaUrl = await downloadInstagramMedia(url);
            if (!platformForMedia) platformForMedia = 'instagram';
          } else if (redditPattern.test(url)) {
            if (!platformForMedia) platformForMedia = 'reddit';
            setMediaContent({
              mediaUrl: null,
              mediaType: 'social_post',
              originalUrl: url,
              platform: platformForMedia,
            });
            setCurrentStep(1);
            directMedia = true;
          } else if (validateUrl(url)) {
            if (url.match(/\.(jpeg|jpg|gif|png)$/i) != null) {
              setMediaContent({
                mediaUrl: url,
                mediaType: 'image/direct',
                originalUrl: url,
                platform: platformForMedia || 'unknown',
              });
              setMediaPreview(url);
              setCurrentStep(1);
              directMedia = true;
            } else {
              if (url.includes('reddit.com')) platformForMedia = 'reddit';
              setMediaContent({
                mediaUrl: null,
                mediaType: 'social_post',
                originalUrl: url,
                platform: platformForMedia || 'other',
              });
              setCurrentStep(1);
              directMedia = true;
            }
          } else {
            setError('Invalid or unsupported URL.');
            setIsConverting(false);
            return;
          }

          if (!directMedia && downloadedMediaUrl) {
            let finalMediaUrlToPrepare = downloadedMediaUrl;
            // Only proxy for Instagram if the URL doesn't seem to be a direct video link already
            // Based on sample, IG photos from looter are .webp, videos are .mp4
            if (
              platformForMedia === 'instagram' &&
              downloadedMediaUrl.includes('.webp')
            ) {
              console.log(
                '[FRONTEND DEBUG handleConversion] Instagram photo (.webp) detected, using proxy for URL:',
                downloadedMediaUrl
              );
              finalMediaUrlToPrepare = `${
                API_CONFIG.API_SERVER
              }/api/proxy-media?url=${encodeURIComponent(downloadedMediaUrl)}`;
            } else {
              console.log(
                '[FRONTEND DEBUG handleConversion] Non-proxied media or Instagram video, fetching directly URL:',
                downloadedMediaUrl
              );
            }

            const preparedMedia = await prepareMediaForUpload(
              finalMediaUrlToPrepare
            );
            setMediaContent({
              ...preparedMedia,
              originalUrl: url,
              platform: platformForMedia,
            });
          } else if (directMedia) {
            // Media content already set
          } else if (!mediaContent && validateUrl(url)) {
            setMediaContent({
              originalUrl: url,
              platform: platformForMedia || 'unknown',
              mediaType: 'social_post',
            });
          }
        } catch (error) {
          setError('Failed to process URL: ' + error.message);
        } finally {
          setIsConverting(false);
        }
      };

      handleConversion();
    }
  }, [isConverting, url, selectedPlatform]);

  // Check for OAuth 1.0a callback on mount
  useEffect(() => {
    const storedAuth = localStorage.getItem('x_auth');
    if (storedAuth) {
      try {
        const authData = JSON.parse(storedAuth);
        setXAuthUser(authData);
      } catch (e) {
        localStorage.removeItem('x_auth');
      }
    }

    // Check for OAuth callback parameters in URL
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('access_token') || urlParams.has('error')) {
      processOAuthCallback();
    }
  }, []);

  // Process OAuth 1.0a callback
  const processOAuthCallback = async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const accessToken = urlParams.get('access_token');
      const accessSecret = urlParams.get('access_secret');
      const error = urlParams.get('error');

      if (error) {
        throw new Error(decodeURIComponent(error));
      }

      if (!accessToken || !accessSecret) {
        throw new Error('Missing OAuth credentials in callback');
      }

      // Get user data using OAuth 1.0a credentials
      const userResponse = await fetch(
        `${API_CONFIG.API_SERVER}/api/users/me?accessToken=${accessToken}&accessSecret=${accessSecret}`
      );

      if (!userResponse.ok) {
        throw new Error('Failed to get user data');
      }

      const userData = await userResponse.json();

      const authData = {
        access_token: accessToken,
        access_secret: accessSecret,
        ...userData.data,
      };

      // Store auth data in localStorage
      localStorage.setItem('x_auth', JSON.stringify(authData));

      // Update state
      setXAuthUser(authData);
      console.log(
        '[Frontend] OAuth 1.0a flow complete. xAuthUser set:',
        authData
      );

      // Clean URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (error) {
      console.error('Error during Twitter OAuth callback:', error);
      setError(`Failed to connect your X account: ${error.message}`);
    }
  };

  // Function to handle scroll events for parallax effects
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const validateUrl = (inputUrl) => {
    const tiktokPattern =
      /^(https?:\/\/)?(www\.)?(tiktok\.com)\/@?[a-zA-Z0-9_.]+\/video\/\d+/;
    const instagramPattern =
      /^(https?:\/\/)?(www\.)?(instagram\.com|instagr\.am)\/(p|reel|reels)\/[a-zA-Z0-9_-]+\/?/;
    const redditPattern =
      /^(https?:\/\/)?(www\.)?(reddit\.com|redd\.it)\/r\/[a-zA-Z0-9_]+\/(comments\/)?([a-zA-Z0-9_]+)(\/[\w\W]*)?$/;
    // Basic check for any http/https URL for broader acceptance if not platform specific
    const genericUrlPattern =
      /^(https?:\/\/)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/;

    return (
      tiktokPattern.test(inputUrl) ||
      instagramPattern.test(inputUrl) ||
      redditPattern.test(inputUrl) ||
      genericUrlPattern.test(inputUrl) // Allow any valid-looking URL for flexibility
    );
  };

  const handleInputChange = (e) => {
    setUrl(e.target.value);
    setError('');
  };

  const handleTickerChange = (e) => {
    const value = e.target.value.toUpperCase();
    setTicker(value);
    setTickerError('');
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    setName(value);
    setNameError('');
  };

  // Handle Twitter/X OAuth 1.0a
  const initiateXAuth = async () => {
    try {
      // Get request token
      const response = await fetch(
        `${API_CONFIG.API_SERVER}/api/oauth/request_token`,
        {
          method: 'POST',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to get request token');
      }

      const { oauth_token, oauth_token_secret } = await response.json();

      // Store token secret for later
      localStorage.setItem('oauth_token_secret', oauth_token_secret);

      // Redirect to Twitter auth page
      const authUrl = `https://api.twitter.com/oauth/authorize?oauth_token=${oauth_token}`;
      window.location.href = authUrl;
    } catch (error) {
      console.error('Error during X authentication:', error);
      setError('Failed to connect your X account. Please try again.');
    }
  };

  // Handle logout
  const handleXLogout = () => {
    console.log('[Frontend] Logging out from X.');
    localStorage.removeItem('x_auth');
    localStorage.removeItem('oauth_token_secret'); // Clear OAuth 1.0a token secret
    setXAuthUser(null);
    setCurrentStep(0);
    setError('');
    setMediaContent(null);
    setMediaPreview(null);
  };

  const handleUrlSubmit = () => {
    if (!url) {
      setError('Please insert a valid social media post URL');
      return;
    }
    if (!validateUrl(url)) {
      setError('Invalid URL format. Please enter a valid URL.');
      return;
    }
    if (!xAuthUser) {
      setError('Please connect your X account before tokenizing content.');
      return;
    }
    setCurrentStep(1); // Move to combined preview/form step
    setIsConverting(true);
    setError('');
  };

  const handleTokenFormSubmit = async () => {
    try {
      setIsPosting(true);
      setTickerError('');
      setNameError('');

      if (!ticker) {
        setTickerError('Ticker is required');
        setIsPosting(false); // Added to ensure loading state stops
        return;
      }
      if (!name) {
        setNameError('Name is required');
        setIsPosting(false); // Added to ensure loading state stops
        return;
      }
      if (!mediaContent) {
        setError('No media content available for tokenization.');
        setIsPosting(false); // Added to ensure loading state stops
        return;
      }

      // console.log('[FRONTEND DEBUG] handleTokenFormSubmit: Preparing to upload. xAuthUser:', xAuthUser);
      // console.log('[FRONTEND DEBUG] handleTokenFormSubmit: Media content:', {
      //   mediaDataType: typeof mediaContent.mediaData,
      //   mediaDataLength: mediaContent.mediaData ? mediaContent.mediaData.length : 0,
      //   mediaType: mediaContent.mediaType
      // });

      if (!xAuthUser || !xAuthUser.access_token) {
        console.error(
          '[Frontend] X Authentication token is missing for media upload.'
        );
        setError(
          'Authentication token is missing. Please reconnect your X account.'
        );
        setIsPosting(false);
        return;
      }

      // [ROUTE CHANGE] Point to /twitter/upload-media which uses OAuth 1.0a handled by backend
      console.log('[Frontend] Calling /twitter/upload-media for X.');
      const uploadResponse = await fetch(
        `${API_CONFIG.API_SERVER}/twitter/upload-media?accessToken=${xAuthUser.access_token}&accessSecret=${xAuthUser.access_secret}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            mediaData: mediaContent.mediaData,
            mediaType: mediaContent.mediaType,
          }),
        }
      );

      console.log(
        '[Frontend] /twitter/upload-media response status:',
        uploadResponse.status
      );

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json();
        console.error(
          '[Frontend] /twitter/upload-media failed. Error data:',
          errorData
        );
        throw new Error(
          errorData.error ||
            'Failed to upload media to X via /twitter/upload-media'
        );
      }

      const { mediaId } = await uploadResponse.json();
      console.log(
        '[Frontend] Media uploaded via /twitter/upload-media, mediaId for X:',
        mediaId
      );

      // ---- DELAY REMOVED ----

      console.log(
        '[Frontend] xAuthUser state before post-tweet call:',
        JSON.stringify(xAuthUser)
      );
      if (!xAuthUser || !xAuthUser.access_token) {
        // Check again before use
        console.error(
          '[Frontend] X Authentication token is missing just before attempting to post tweet.'
        );
        setError('X Auth token lost. Please reconnect X and try again.');
        setIsPosting(false);
        return;
      }
      console.log(
        '[Frontend] Access token being used for post-tweet:',
        xAuthUser.access_token
          ? xAuthUser.access_token.substring(0, 15) + '...'
          : 'TOKEN MISSING'
      );
      console.log(
        '[Frontend] Calling /twitter/post-tweet with mediaId for X:',
        mediaId
      );
      const tweetText = `Launched by @useceroapp\n\n@launchcoin $${ticker} + ${name}`;

      const tweetResponse = await fetch(
        `${API_CONFIG.API_SERVER}/twitter/post-tweet?accessToken=${xAuthUser.access_token}&accessSecret=${xAuthUser.access_secret}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            text: tweetText,
            media_id: mediaId,
          }),
        }
      );

      console.log(
        '[Frontend] /twitter/post-tweet response status:',
        tweetResponse.status
      );
      if (!tweetResponse.ok) {
        const errorData = await tweetResponse.json();
        console.error(
          '[Frontend] /twitter/post-tweet failed. Error data:',
          errorData
        );
        throw new Error(errorData.error || 'Failed to post tweet to X');
      }

      setPostSuccess(true);
      setCurrentStep(2);
    } catch (error) {
      console.error(
        '[Frontend] Error in X token form submission pipeline:',
        error
      );
      setError(
        error.message || 'An unexpected error occurred during X operation.'
      );
    } finally {
      setIsPosting(false);
    }
  };

  const handleReset = () => {
    setUrl('');
    setError('');
    setShowTokenForm(false);
    setMediaPreview(null);
    setTicker('');
    setName('');
    setTickerError('');
    setNameError('');
    setPostSuccess(false);
    setCurrentStep(0);
  };

  const ParticleBackground = () => {
    return (
      <div className='absolute inset-0 overflow-hidden pointer-events-none'>
        {/* Animated gradient orbs */}
        <div
          className='absolute w-[800px] h-[800px] rounded-full bg-gradient-to-r from-accent-primary/10 to-accent-secondary/10 blur-3xl -top-[400px] -left-[400px] animate-float-slow'
          style={{ transform: `translateY(${scrollY * 0.1}px)` }}
        ></div>
        <div
          className='absolute w-[600px] h-[600px] rounded-full bg-gradient-to-r from-accent-secondary/10 to-accent-light/10 blur-3xl -bottom-[300px] -right-[300px] animate-float-medium'
          style={{ transform: `translateY(${-scrollY * 0.05}px)` }}
        ></div>

        {/* Animated dots grid */}
        <div className='absolute inset-0' style={{ opacity: 0.4 }}>
          <div className='relative w-full h-full'>
            {Array.from({ length: 50 }).map((_, i) => (
              <div
                key={i}
                className='absolute w-1 h-1 bg-accent-primary/20 rounded-full animate-pulse'
                style={{
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`,
                }}
              ></div>
            ))}
          </div>
        </div>

        {/* Animated lines */}
        <div
          className='absolute right-0 top-1/3 w-full h-32 bg-gradient-to-r from-transparent via-accent-primary/10 to-transparent skew-y-3 animate-slide-slow'
          style={{ transform: `translateY(${-scrollY * 0.2}px) skewY(3deg)` }}
        ></div>
        <div
          className='absolute left-0 bottom-1/3 w-full h-24 bg-gradient-to-r from-transparent via-accent-secondary/10 to-transparent -skew-y-3 animate-slide-medium'
          style={{ transform: `translateY(${-scrollY * 0.15}px) skewY(-3deg)` }}
        ></div>

        {/* Radial gradient overlay */}
        <div className='absolute inset-0 bg-gradient-radial from-transparent via-dark-base/50 to-dark-base pointer-events-none'></div>
      </div>
    );
  };

  return (
    <div className='flex flex-col items-center min-h-screen bg-dark-base relative overflow-hidden'>
      <ParticleBackground />
      <motion.nav
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className='w-full bg-dark-surface/80 backdrop-blur-md shadow-sm border-b border-dark py-4 z-10 sticky top-0'
      >
        <div className='container mx-auto px-4 flex justify-between items-center'>
          <div className='flex items-center space-x-2'>
            <div className='relative'>
              <img
                src={logoImage}
                alt='Logo'
                className='relative w-10 h-10 transform hover:scale-105 transition-transform duration-300'
                // style={{
                //   animation: 'float 6s ease-in-out infinite'
                // }}
              />
            </div>
          </div>

          <div className='hidden md:flex items-center space-x-6'>
            <motion.a
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              href='#'
              className='text-dark-text-secondary hover:text-accent-primary transition-colors font-medium'
            >
              How it Works
            </motion.a>
            {xAuthUser ? (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleXLogout}
                className='bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-base px-4 py-2 rounded-md font-medium shadow-md hover:shadow-lg transition-all'
              >
                Connected as @{xAuthUser.username}
              </motion.button>
            ) : (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={initiateXAuth}
                className='bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-base px-4 py-2 rounded-md font-medium shadow-md hover:shadow-lg transition-all'
              >
                Connect your X
              </motion.button>
            )}
          </div>

          <div className='md:hidden'>
            <button className='text-dark-text-primary'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='h-6 w-6'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M4 6h16M4 12h16M4 18h16'
                />
              </svg>
            </button>
          </div>
        </div>
      </motion.nav>

      <div className='container mx-auto px-4 py-16 max-w-3xl z-10 mt-8'>
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className='text-center mb-12'
        >
          <h1 className='text-4xl md:text-5xl font-bold mb-4'>
            <span className='bg-gradient-to-r from-accent-primary via-accent-secondary to-accent-lime bg-clip-text text-transparent bg-size-200 animate-gradient-x'>
              Coin everything from TikTok, Instagram, and Reddit
            </span>
          </h1>
          {/* <p className='mt-2 text-xl text-accent-primary font-medium'>
            through Cero
          </p> */}

          <div className='flex justify-center gap-8 mt-8'>
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.6 }}
              className='bg-dark-surface/80 backdrop-blur-sm shadow-md rounded-xl p-4 border border-dark flex flex-col items-center'
            >
              <div className='w-10 h-10 mb-1 flex items-center justify-center'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='h-8 w-8 text-accent-primary'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <circle cx='12' cy='12' r='10' />
                  <polyline points='12 6 12 12 16 14' />
                </svg>
              </div>
              <p className='text-2xl font-bold text-accent-primary'>&lt;30s</p>
              <p className='text-dark-text-secondary'>Virtually Instant</p>
            </motion.div>
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4, delay: 1 }}
              className='bg-dark-surface/80 backdrop-blur-sm shadow-md rounded-xl p-4 border border-dark flex flex-col items-center'
            >
              <div className='w-10 h-10 mb-1 flex items-center justify-center'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='h-8 w-8 text-accent-secondary'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <path d='M20 7h-9'></path>
                  <path d='M14 17H5'></path>
                  <circle cx='17' cy='17' r='3'></circle>
                  <circle cx='7' cy='7' r='3'></circle>
                </svg>
              </div>
              <p className='text-2xl font-bold text-accent-secondary'>3</p>
              <p className='text-dark-text-secondary'>Platforms Supported</p>
            </motion.div>
          </div>
        </motion.div>

        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className='bg-dark-surface/90 backdrop-blur-md rounded-2xl shadow-xl p-8 mb-16 border border-dark relative overflow-hidden'
        >
          <div className='absolute inset-0 overflow-hidden'>
            <div className='absolute w-[500px] h-[500px] -right-48 -top-48 bg-gradient-to-br from-purple-500/10 to-rose-500/10 rounded-full blur-3xl'></div>
            <div className='absolute w-[400px] h-[400px] -left-48 -bottom-48 bg-gradient-to-tr from-orange-500/10 to-amber-500/10 rounded-full blur-3xl'></div>
            <div className='absolute inset-0'>
              {Array.from({ length: 20 }).map((_, i) => (
                <div
                  key={i}
                  className='absolute w-1 h-1 bg-accent-primary/20 rounded-full animate-float-particle'
                  style={{
                    top: `${Math.random() * 100}%`,
                    left: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 5}s`,
                    animationDuration: `${5 + Math.random() * 5}s`,
                  }}
                />
              ))}
            </div>
          </div>

          <div className='relative z-10 mb-12'>
            <div className='flex justify-between items-center max-w-2xl mx-auto'>
              {steps.map((step, index) => (
                <div
                  key={step.title}
                  className={`flex flex-col items-center w-full ${
                    index <= currentStep
                      ? 'text-accent-primary'
                      : 'text-gray-400'
                  }`}
                >
                  <div
                    className={`
                      relative flex items-center justify-center w-10 h-10 rounded-full 
                      ${
                        index <= currentStep
                          ? 'bg-accent-primary text-dark-base'
                          : 'bg-dark-surface border border-dark/60'
                      }
                      transition-all duration-300
                    `}
                  >
                    <span className='text-sm font-medium'>{index + 1}</span>
                    {index < steps.length - 1 && (
                      <div
                        className={`
                          absolute left-10 w-[calc(100%+2rem)] h-[2px]
                          ${
                            index < currentStep
                              ? 'bg-accent-primary'
                              : 'bg-dark/60'
                          }
                        `}
                      />
                    )}
                  </div>
                  <span className='mt-3 text-xs'>{step.title}</span>
                  <span className='text-xs text-dark-text-secondary'>
                    {step.description}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className='relative z-10'>
            {currentStep === 0 && (
              <div className='text-center'>
                <h2 className='text-2xl font-bold mb-4 text-accent-primary'>
                  Supported Platforms
                </h2>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
                  {/* TikTok Card */}
                  <div
                    className={`bg-dark-surface/90 backdrop-blur-md p-6 rounded-lg border border-dark hover:border-accent-primary transition-all group`}
                  >
                    <div className='w-16 h-16 mx-auto mb-4'>
                      <svg
                        viewBox='0 0 24 24'
                        fill='none'
                        className='w-full h-full text-accent-primary group-hover:text-accent-secondary transition-colors'
                      >
                        <path
                          d='M19.589 6.686a4.793 4.793 0 0 1-3.77-4.245V2h-3.445v13.672a2.896 2.896 0 0 1-5.201 1.743l-.002-.001.002.001a2.895 2.895 0 0 1 3.183-4.51v-3.5a6.329 6.329 0 0 0-5.394 10.692 6.33 6.33 0 0 0 10.857-4.424V8.687a8.182 8.182 0 0 0 4.773 1.526V6.79a4.831 4.831 0 0 1-1.003-.104z'
                          fill='currentColor'
                        />
                      </svg>
                    </div>
                    <h3 className='text-lg font-semibold mb-2 text-dark-text-primary group-hover:text-accent-primary transition-colors'>
                      TikTok
                    </h3>
                    <p className='text-sm text-dark-text-secondary'>
                      Convert your TikTok videos into tokens
                    </p>
                  </div>
                  {/* Instagram Card */}
                  <div
                    className={`bg-dark-surface/90 backdrop-blur-md p-6 rounded-lg border border-dark hover:border-accent-primary transition-all group`}
                  >
                    <div className='w-16 h-16 mx-auto mb-4'>
                      <svg
                        viewBox='0 0 24 24'
                        fill='none'
                        className='w-full h-full text-accent-primary group-hover:text-accent-secondary transition-colors'
                      >
                        <path
                          fillRule='evenodd'
                          clipRule='evenodd'
                          d='M12 2C9.284 2 8.944 2.01 7.877 2.06C6.813 2.11 6.088 2.278 5.449 2.525C4.788 2.779 4.2 3.123 3.616 3.707C3.032 4.291 2.688 4.879 2.434 5.54C2.187 6.179 2.019 6.904 1.969 7.968C1.919 9.035 1.909 9.375 1.909 12.091C1.909 14.807 1.919 15.147 1.969 16.214C2.019 17.278 2.187 18.003 2.434 18.642C2.688 19.303 3.032 19.891 3.616 20.475C4.2 21.059 4.788 21.403 5.449 21.657C6.088 21.904 6.813 22.072 7.877 22.122C8.944 22.172 9.284 22.182 12 22.182C14.716 22.182 15.056 22.172 16.123 22.122C17.187 22.072 17.912 21.904 18.551 21.657C19.212 21.403 19.8 21.059 20.384 20.475C20.968 19.891 21.312 19.303 21.566 18.642C21.813 18.003 21.981 17.278 22.031 16.214C22.081 15.147 22.091 14.807 22.091 12.091C22.091 9.375 22.081 9.035 22.031 7.968C21.981 6.904 21.813 6.179 21.566 5.54C21.312 4.879 20.968 4.291 20.384 3.707C19.8 3.123 19.212 2.779 18.551 2.525C17.912 2.278 17.187 2.11 16.123 2.06C15.056 2.01 14.716 2 12 2ZM12 3.802C14.67 3.802 14.987 3.812 16.041 3.861C17.015 3.907 17.551 4.067 17.906 4.204C18.373 4.381 18.703 4.592 19.048 4.937C19.393 5.282 19.604 5.612 19.781 6.079C19.918 6.434 20.078 6.97 20.124 7.944C20.173 8.998 20.183 9.315 20.183 11.985C20.183 14.655 20.173 14.972 20.124 16.026C20.078 17 19.918 17.536 19.781 17.891C19.604 18.358 19.393 18.688 19.048 19.033C18.703 19.378 18.373 19.589 17.906 19.766C17.551 19.903 17.015 20.063 16.041 20.109C14.987 20.158 14.67 20.168 12 20.168C9.33 20.168 9.013 20.158 7.959 20.109C6.985 20.063 6.449 19.903 6.094 19.766C5.627 19.589 5.297 19.378 4.952 19.033C4.607 18.688 4.396 18.358 4.219 17.891C4.082 17.536 3.922 17 3.876 16.026C3.827 14.972 3.817 14.655 3.817 11.985C3.817 9.315 3.827 8.998 3.876 7.944C3.922 6.97 4.082 6.434 4.219 6.079C4.396 5.612 4.607 5.282 4.952 4.937C5.297 4.592 5.627 4.381 6.094 4.204C6.449 4.067 6.985 3.907 7.959 3.861C9.013 3.812 9.33 3.802 12 3.802Z'
                          fill='currentColor'
                        />
                        <path
                          fillRule='evenodd'
                          clipRule='evenodd'
                          d='M12 15.336C10.159 15.336 8.667 13.844 8.667 12.003C8.667 10.162 10.159 8.67 12 8.67C13.841 8.67 15.333 10.162 15.333 12.003C15.333 13.844 13.841 15.336 12 15.336ZM12 6.868C9.165 6.868 6.865 9.168 6.865 12.003C6.865 14.838 9.165 17.138 12 17.138C14.835 17.138 17.135 14.838 17.135 12.003C17.135 9.168 14.835 6.868 12 6.868Z'
                          fill='currentColor'
                        />
                        <path
                          d='M17.338 7.862C18.0273 7.862 18.588 7.30134 18.588 6.612C18.588 5.92266 18.0273 5.362 17.338 5.362C16.6487 5.362 16.088 5.92266 16.088 6.612C16.088 7.30134 16.6487 7.862 17.338 7.862Z'
                          fill='currentColor'
                        />
                      </svg>
                    </div>
                    <h3 className='text-lg font-semibold mb-2 text-dark-text-primary group-hover:text-accent-primary transition-colors'>
                      Instagram
                    </h3>
                    <p className='text-sm text-dark-text-secondary'>
                      Turn your Instagram content into tokens
                    </p>
                  </div>
                  {/* Reddit Card */}
                  <div
                    className={`bg-dark-surface/90 backdrop-blur-md p-6 rounded-lg border border-dark hover:border-accent-primary transition-all group`}
                  >
                    <div className='w-16 h-16 mx-auto mb-4'>
                      <svg
                        viewBox='0 0 24 24'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                        className='w-full h-full text-accent-primary group-hover:text-accent-secondary transition-colors'
                      >
                        {/* [SVG UNIFORMITY] Wrapped path in a <g> with a scale transform */}
                        <g transform='translate(12 12) scale(1.1) translate(-12 -12)'>
                          <path
                            d='M10.24 13.6C10.0818 13.6 9.92711 13.5531 9.79555 13.4652C9.66399 13.3773 9.56145 13.2523 9.5009 13.1061C9.44035 12.96 9.42451 12.7991 9.45538 12.6439C9.48624 12.4887 9.56244 12.3462 9.67432 12.2343C9.7862 12.1224 9.92875 12.0462 10.0839 12.0154C10.2391 11.9845 10.4 12.0003 10.5461 12.0609C10.6923 12.1214 10.8173 12.224 10.9052 12.3555C10.9931 12.4871 11.04 12.6418 11.04 12.8C11.04 12.9051 11.0193 13.0091 10.9791 13.1061C10.9389 13.2032 10.88 13.2914 10.8057 13.3657C10.7314 13.44 10.6432 13.4989 10.5461 13.5391C10.4491 13.5793 10.3451 13.6 10.24 13.6ZM20 12C20 13.5823 19.5308 15.129 18.6518 16.4446C17.7727 17.7602 16.5233 18.7855 15.0615 19.391C13.5997 19.9965 11.9911 20.155 10.4393 19.8463C8.88743 19.5376 7.46197 18.7757 6.34315 17.6569C5.22433 16.538 4.4624 15.1126 4.15372 13.5607C3.84504 12.0089 4.00347 10.4003 4.60897 8.93853C5.21447 7.47672 6.23985 6.22729 7.55544 5.34824C8.87103 4.46919 10.4178 4 12 4C14.1217 4 16.1566 4.84285 17.6569 6.34315C19.1571 7.84344 20 9.87827 20 12ZM15.73 10.67C15.4543 10.6862 15.1943 10.8038 15 11C14.1775 10.4564 13.2158 10.1613 12.23 10.15L12.79 7.62L14.58 8.02C14.5787 8.12458 14.5981 8.22839 14.6372 8.32539C14.6764 8.4224 14.7343 8.51067 14.8078 8.58509C14.8813 8.65951 14.9689 8.7186 15.0654 8.75892C15.1619 8.79925 15.2654 8.82001 15.37 8.82C15.5831 8.81737 15.7866 8.73087 15.9363 8.57925C16.0861 8.42763 16.17 8.2231 16.17 8.01C16.176 7.82715 16.1183 7.6479 16.0069 7.50284C15.8954 7.35777 15.737 7.25589 15.5588 7.21458C15.3806 7.17327 15.1936 7.1951 15.0296 7.27633C14.8657 7.35756 14.7351 7.49317 14.66 7.66L12.66 7.22C12.6124 7.21087 12.5631 7.22027 12.5222 7.2463C12.4813 7.27233 12.4519 7.313 12.44 7.36L11.82 10.15C10.8346 10.1641 9.87368 10.4589 9.05 11C8.94175 10.8882 8.81043 10.8014 8.66518 10.7456C8.51993 10.6898 8.36426 10.6663 8.20902 10.6769C8.05378 10.6874 7.9027 10.7316 7.76631 10.8065C7.62993 10.8814 7.51152 10.9852 7.41934 11.1105C7.32717 11.2359 7.26344 11.3799 7.23261 11.5324C7.20178 11.6849 7.20458 11.8423 7.24082 11.9936C7.27706 12.1449 7.34586 12.2865 7.44244 12.4085C7.53902 12.5305 7.66104 12.63 7.8 12.7C7.78471 12.8663 7.78471 13.0337 7.8 13.2C7.8 14.89 9.71 16.27 12.06 16.27C14.41 16.27 16.32 14.89 16.32 13.2C16.3204 13.0282 16.3002 12.857 16.26 12.69C16.4621 12.5755 16.6215 12.3984 16.714 12.1854C16.8066 11.9724 16.8274 11.7351 16.7732 11.5092C16.719 11.2834 16.5927 11.0813 16.4136 10.9335C16.2344 10.7858 16.012 10.7003 15.78 10.69L15.73 10.67ZM13.51 14.42C13.0606 14.7023 12.5407 14.852 12.01 14.852C11.4793 14.852 10.9594 14.7023 10.51 14.42C10.4731 14.3863 10.425 14.3676 10.375 14.3676C10.325 14.3676 10.2769 14.3863 10.24 14.42C10.2206 14.4378 10.2051 14.4594 10.1945 14.4835C10.1839 14.5076 10.1785 14.5337 10.1785 14.56C10.1785 14.5863 10.1839 14.6124 10.1945 14.6365C10.2051 14.6606 10.2206 14.6822 10.24 14.7C10.7652 15.0564 11.3853 15.2469 12.02 15.2469C12.6547 15.2469 13.2748 15.0564 13.8 14.7C13.8194 14.6822 13.8349 14.6606 13.8455 14.6365C13.8561 14.6124 13.8616 14.5863 13.8616 14.56C13.8616 14.5337 13.8561 14.5076 13.8455 14.4835C13.8349 14.4594 13.8194 14.4378 13.8 14.42C13.7813 14.4003 13.7588 14.3847 13.7339 14.374C13.709 14.3633 13.6821 14.3577 13.655 14.3577C13.6279 14.3577 13.601 14.3633 13.5761 14.374C13.5512 14.3847 13.5287 14.4003 13.51 14.42ZM13.76 12C13.6018 12 13.4471 12.0469 13.3155 12.1348C13.184 12.2227 13.0814 12.3477 13.0209 12.4939C12.9604 12.64 12.9445 12.8009 12.9754 12.9561C13.0062 13.1113 13.0824 13.2538 13.1943 13.3657C13.3062 13.4776 13.4487 13.5538 13.6039 13.5846C13.7591 13.6155 13.92 13.5997 14.0662 13.5391C14.2123 13.4786 14.3373 13.376 14.4252 13.2445C14.5131 13.1129 14.56 12.9582 14.56 12.8C14.56 12.5878 14.4757 12.3843 14.3257 12.2343C14.1757 12.0843 13.9722 12 13.76 12Z'
                            fill='currentColor'
                          />
                        </g>
                      </svg>
                    </div>
                    <h3 className='text-lg font-semibold mb-2 text-dark-text-primary group-hover:text-accent-primary transition-colors'>
                      Reddit
                    </h3>
                    <p className='text-sm text-dark-text-secondary'>
                      Convert Reddit posts into tokens
                    </p>
                  </div>
                </div>
                <div className='mt-8'>
                  <div className='relative'>
                    <div className='absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        className='h-5 w-5 text-dark-text-secondary'
                        fill='none'
                        viewBox='0 0 24 24'
                        stroke='currentColor'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1'
                        />
                      </svg>
                    </div>
                    <input
                      type='text'
                      value={url}
                      onChange={handleInputChange}
                      placeholder='Paste your content URL here...'
                      className='w-full p-4 pl-12 border border-dark/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-primary bg-dark-base text-dark-text-primary shadow-sm'
                    />
                  </div>
                  <button
                    onClick={handleUrlSubmit}
                    disabled={isConverting || !validateUrl(url) || !url}
                    className={`mt-4 px-6 py-2 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-base rounded-lg transition-all ${
                      isConverting || !validateUrl(url) || !url
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:opacity-90'
                    }`}
                  >
                    {isConverting ? 'Processing...' : 'Continue'}
                  </button>
                </div>
              </div>
            )}

            {currentStep === 1 && (
              <div className='grid md:grid-cols-2 gap-8 items-stretch'>
                <div
                  className={`bg-dark-surface/90 backdrop-blur-md p-6 rounded-lg border border-dark flex flex-col ${
                    isConverting ? 'md:col-span-2 text-center' : ''
                  }`}
                >
                  <h2 className='text-2xl font-bold mb-6 text-accent-primary text-center md:text-left'>
                    {isConverting
                      ? 'Processing Content...'
                      : mediaContent?.mediaUrl
                      ? 'Content Preview'
                      : 'Content Information'}
                  </h2>
                  {isConverting ? (
                    <div className='flex-grow flex flex-col items-center justify-center py-12'>
                      <div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent-primary'></div>
                      <p className='mt-4 text-dark-text-secondary'>
                        Fetching and preparing media...
                      </p>
                    </div>
                  ) : mediaContent ? (
                    <div className='flex-grow flex flex-col justify-center'>
                      {mediaContent.mediaUrl ? (
                        mediaContent.mediaType?.includes('video') ? (
                          <video
                            src={mediaContent.mediaUrl}
                            controls
                            className='max-w-full w-full mx-auto max-h-[300px] rounded-lg object-contain'
                          />
                        ) : (
                          <img
                            src={mediaContent.mediaUrl}
                            alt='Content preview'
                            className='max-w-full w-full mx-auto max-h-[300px] rounded-lg object-contain'
                          />
                        )
                      ) : (
                        <div className='text-center py-8'>
                          <p className='text-dark-text-secondary text-lg mb-2'>
                            No direct preview available.
                          </p>
                          <p className='text-dark-text-primary text-sm break-all'>
                            Original URL:{' '}
                            <a
                              href={mediaContent.originalUrl}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='text-accent-primary hover:underline'
                            >
                              {mediaContent.originalUrl}
                            </a>
                          </p>
                          <p className='text-dark-text-secondary text-sm'>
                            Platform:{' '}
                            {mediaContent.platform || selectedPlatform || 'N/A'}
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className='flex-grow flex flex-col items-center justify-center py-12'>
                      <p className='mt-4 text-dark-text-secondary'>
                        Could not load media preview.
                      </p>
                      <button
                        onClick={() => {
                          setCurrentStep(0);
                          setError('');
                        }}
                        className='mt-4 px-6 py-2 bg-dark-surface text-dark-text-primary rounded-lg hover:bg-dark-surface/80 transition-all'
                      >
                        Go Back
                      </button>
                    </div>
                  )}
                </div>
                {!isConverting && mediaContent && (
                  <div className='bg-dark-surface/90 backdrop-blur-md p-6 rounded-lg border border-dark flex flex-col'>
                    <h2 className='text-2xl font-bold mb-6 text-accent-primary text-center md:text-left'>
                      Token Details
                    </h2>
                    <TokenForm
                      ticker={ticker}
                      name={name}
                      handleTickerChange={handleTickerChange}
                      handleNameChange={handleNameChange}
                      tickerError={tickerError}
                      nameError={nameError}
                      isPosting={isPosting}
                      handleTokenFormSubmit={handleTokenFormSubmit}
                      initialFocusRef={tokenFormTickerRef}
                    />
                  </div>
                )}
              </div>
            )}

            {currentStep === 2 && postSuccess && (
              <div className='text-center'>
                <div className='mb-4'>
                  <CheckCircle className='w-16 h-16 text-green-500 mx-auto' />
                </div>
                <h2 className='text-2xl font-bold mb-2'>Success!</h2>
                <p className='text-gray-300 mb-4'>
                  Your content has been tokenized and posted to Twitter.
                </p>
                <button
                  onClick={handleReset}
                  className='px-6 py-2 bg-accent-primary text-dark-base rounded-lg hover:bg-accent-secondary transition-all'
                >
                  Create Another Token
                </button>
              </div>
            )}

            {error && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className='mt-4 bg-red-900/20 border border-red-800/30 rounded-lg p-3 flex items-center text-red-400'
              >
                <AlertCircle size={20} className='mr-2' />
                <span>{error}</span>
                <button
                  onClick={() => setError('')}
                  className='ml-auto text-red-400 hover:text-red-300'
                >
                  <X size={16} />
                </button>
              </motion.div>
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: '-100px' }}
          className='mb-20'
        >
          <h2 className='text-2xl md:text-3xl font-bold text-center mb-8 text-accent-primary'>
            How It Works
          </h2>

          <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              viewport={{ once: true }}
              className='bg-dark-surface/80 backdrop-blur-sm rounded-xl p-6 shadow-md border border-dark'
            >
              <div className='bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-base rounded-full w-12 h-12 flex items-center justify-center font-bold mb-4 shadow-md'>
                1
              </div>
              <h3 className='text-xl font-semibold text-accent-primary mb-2'>
                Connect X Account
              </h3>
              <p className='text-dark-text-secondary'>
                Once connected, your token is ready to post and integrate with
                Launch a Coin.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              viewport={{ once: true }}
              className='bg-dark-surface/80 backdrop-blur-sm rounded-xl p-6 shadow-md border border-dark'
            >
              <div className='bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-base rounded-full w-12 h-12 flex items-center justify-center font-bold mb-4 shadow-md'>
                2
              </div>
              <h3 className='text-xl font-semibold text-accent-primary mb-2'>
                Paste URL
              </h3>
              <p className='text-dark-text-secondary'>
                Simply paste any TikTok, Instagram, or Reddit URL to begin the
                tokenization process.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
              viewport={{ once: true }}
              className='bg-dark-surface/80 backdrop-blur-sm rounded-xl p-6 shadow-md border border-dark'
            >
              <div className='bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-base rounded-full w-12 h-12 flex items-center justify-center font-bold mb-4 shadow-md'>
                3
              </div>
              <h3 className='text-xl font-semibold text-accent-primary mb-2'>
                Token It
              </h3>
              <p className='text-dark-text-secondary'>
                Your content is now tokenized and ready to be traded on
                supported platforms.
              </p>
            </motion.div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: '-100px' }}
          className='mb-20'
        >
          <h2 className='text-2xl md:text-3xl font-bold text-center mb-8 text-accent-primary'>
            Platform Roadmap
          </h2>

          <div className='relative'>
            <div className='absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-accent-primary via-accent-secondary to-accent-lime rounded-full hidden md:block'></div>

            <div className='space-y-12 relative'>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className='md:grid md:grid-cols-2 md:gap-8 items-center'
              >
                <div className='md:text-right'>
                  <div className='bg-dark-surface/80 backdrop-blur-sm rounded-xl p-6 shadow-md border border-dark mb-4 md:mb-0'>
                    <div className='inline-block bg-accent-primary/20 rounded-full px-3 py-1 text-xs font-semibold text-accent-lime uppercase tracking-wide mb-2'>
                      Q2 2025
                    </div>
                    <h3 className='text-xl font-bold text-accent-primary mb-2'>
                      Platform Launch
                    </h3>
                    <p className='text-dark-text-secondary'>
                      Initial launch with support for TikTok, Instagram, and
                      Reddit content tokenization. Core features include
                      one-click tokenization, wallet integration, and basic
                      marketplace listings.
                    </p>
                  </div>
                </div>
                <div className='hidden md:flex justify-start items-center'>
                  <div className='w-10 h-10 bg-accent-primary rounded-full shadow-md flex items-center justify-center text-dark-base font-bold z-10'>
                    1
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className='md:grid md:grid-cols-2 md:gap-8 items-center'
              >
                <div className='hidden md:flex justify-end items-center'>
                  <div className='w-10 h-10 bg-accent-secondary rounded-full shadow-md flex items-center justify-center text-dark-base font-bold z-10'>
                    2
                  </div>
                </div>
                <div>
                  <div className='bg-dark-surface/80 backdrop-blur-sm rounded-xl p-6 shadow-md border border-dark mb-4 md:mb-0'>
                    <div className='inline-block bg-accent-primary/20 rounded-full px-3 py-1 text-xs font-semibold text-accent-lime uppercase tracking-wide mb-2'>
                      Upcoming
                    </div>
                    <h3 className='text-xl font-bold text-accent-primary mb-2'>
                      Platform Expansion
                    </h3>
                    <p className='text-dark-text-secondary'>
                      We're exploring integration possibilities with additional
                      content platforms beyond our initial three. Our team is
                      actively researching which platforms creators value most
                      for their monetization strategies.
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className='md:grid md:grid-cols-2 md:gap-8 items-center'
              >
                <div className='md:text-right'>
                  <div className='bg-dark-surface/80 backdrop-blur-sm rounded-xl p-6 shadow-md border border-dark'>
                    <div className='inline-block bg-accent-primary/20 rounded-full px-3 py-1 text-xs font-semibold text-accent-lime uppercase tracking-wide mb-2'>
                      Community-Driven
                    </div>
                    <h3 className='text-xl font-bold text-accent-primary mb-2'>
                      The Future of Content Tokens
                    </h3>
                    <p className='text-dark-text-secondary'>
                      This is where you come in. We're building our long-term
                      roadmap based on creator needs and community feedback.
                      Join our early access program to help shape the future of
                      content tokenization.
                    </p>
                  </div>
                </div>
                <div className='hidden md:flex justify-start items-center'>
                  <div className='w-10 h-10 bg-accent-secondary rounded-full shadow-md flex items-center justify-center text-dark-base font-bold z-10'>
                    ?
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>

      <footer className='w-full bg-dark-surface/80 backdrop-blur-md py-8 border-t border-dark z-10 mt-auto'>
        <div className='container mx-auto px-4'>
          <div className='grid grid-cols-1 md:grid-cols-4 gap-8'>
            <div>
              <h3 className='font-bold text-lg mb-4 text-dark-text-primary'>
                Cero
              </h3>
              <p className='text-dark-text-secondary'>Coin Everything.</p>
            </div>
          </div>

          <div className='mt-8 pt-6 border-t border-dark text-center text-dark-text-secondary'>
            <p>© 2025 Cero. All rights reserved. </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Cero;
